import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search, ShieldCheck, ChevronRight } from "lucide-react";
import HeaderBar from "../students/components/headerBar";
import ServiceDetailView from "./ServiceDetailView";
import { useVerificationServicesQuery } from "../../hooks/queries/useVerificationQueries";

// ─── Service Card ─────────────────────────────────────────────────────────────
const ServiceCard = ({ service, onSelect }) => {
  const isSandbox = service.environment === "SANDBOX";
  const isNin =
    service.slug?.toLowerCase().includes("nin") ||
    service.name?.toLowerCase().includes("nin") ||
    service.category === "Identity";
  const variantCount =
    service.sub_services && service.sub_services.length > 0
      ? service.sub_services.length
      : isNin
      ? 4
      : 0;

  return (
    <div
      onClick={() => onSelect(service)}
      className={`cht-bg-white cht-rounded-squircle cht-shadow-soft cht-p-6 cht-border cht-transition-all cht-duration-300 cht-group cht-cursor-pointer hover:-cht-translate-y-1 ${isSandbox ? "cht-border-amber-100 hover:cht-border-amber-300" : "cht-border-gray-100 hover:cht-border-[#3e57da]/30"
        }`}
    >
      <div className="cht-flex cht-items-start cht-gap-4 cht-mb-4">
        <div className={`cht-p-3 cht-rounded-xl group-hover:cht-scale-110 cht-transition-transform cht-shrink-0 ${isSandbox ? "cht-bg-amber-50 cht-text-amber-600" : "cht-bg-[#f2f4fd] cht-text-[#3e57da]"
          }`}>
          <ShieldCheck size={22} />
        </div>
        <div className="cht-flex-1 cht-min-w-0">
          <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
            <h4 className="cht-font-bold cht-text-gray-900 cht-leading-snug">{service.name}</h4>
            {isSandbox && (
              <span className="cht-text-xs cht-italic cht-font-medium cht-text-amber-600 cht-bg-amber-50 cht-border cht-border-amber-200 cht-px-2 cht-py-0.5 cht-rounded-full">
                Test Mode
              </span>
            )}
          </div>
          <span className="cht-text-xs cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-gray-600 cht-rounded-full cht-mt-1 cht-inline-block">
            {service.category}
          </span>
        </div>
      </div>

      {variantCount > 0 && (
        <div className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-mb-3">
          <span className="cht-text-gray-400">Contains {variantCount} variants</span>
          {isNin && (
            <span className="cht-text-[10px] cht-font-bold cht-text-emerald-700 cht-bg-emerald-50 cht-px-2 cht-py-0.5 cht-rounded-full cht-border cht-border-emerald-200">
              Visual Samples
            </span>
          )}
        </div>
      )}

      <div className="cht-flex cht-justify-between cht-items-center cht-pt-4 cht-border-t cht-border-gray-100">
        <span className={`cht-font-bold ${isSandbox ? 'cht-text-amber-600' : 'cht-text-[#3e57da]'}`}>
          {isSandbox ? "₦0.00 (Test)" : `₦${parseFloat(service.cost).toFixed(2)}`}
        </span>
        <span className={`cht-flex cht-items-center cht-gap-1 cht-text-xs cht-font-semibold cht-transition-colors ${isSandbox ? "cht-text-amber-600 group-hover:cht-text-amber-800" : "cht-text-[#3e57da] group-hover:cht-text-[#3248c0]"
          }`}>
          Configure Options <ChevronRight size={14} />
        </span>
      </div>
    </div>
  );
};

// ─── Skeleton Card ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-p-6 cht-border cht-border-gray-100 cht-animate-pulse">
    <div className="cht-flex cht-gap-4 cht-mb-4">
      <div className="cht-h-12 cht-w-12 cht-rounded-xl cht-bg-gray-200" />
      <div className="cht-flex-1">
        <div className="cht-h-4 cht-bg-gray-200 cht-rounded cht-mb-2" />
        <div className="cht-h-3 cht-w-16 cht-bg-gray-100 cht-rounded" />
      </div>
    </div>
    <div className="cht-h-px cht-bg-gray-100 cht-mb-3" />
    <div className="cht-h-4 cht-w-20 cht-bg-gray-200 cht-rounded" />
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const CATEGORIES = ["All", "Identity", "Financial", "Business", "Utility"];

const ServicesPage = () => {
  const { data: servicesData = [], isLoading: loading } = useVerificationServicesQuery();
  const services = Array.isArray(servicesData) ? servicesData : [];
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSubService, setSelectedSubService] = useState(null);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.service) {
      setSelectedService(location.state.service);
      if (location.state?.subService) {
        setSelectedSubService(location.state.subService);
      }
    }
  }, [location.state]);

  const filtered = services.filter((s) => {
    const matchCat = category === "All" || s.category === category;
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader={selectedService ? `Services / ${selectedService.name}` : "API Services"} />
      <main className="cht-container cht-mx-auto cht-mb-12">

        {/* Toggle between Grid Dashboard View and Specialized Product Detail View Context */}
        {selectedService ? (
          <ServiceDetailView
            service={selectedService}
            selectedSubService={selectedSubService}
            setSelectedSubService={setSelectedSubService}
            onBack={() => {
              setSelectedService(null);
              setSelectedSubService(null);
            }}
          />
        ) : (
          <>
            {/* Filters */}
            <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-3 cht-mb-6">
              <div className="cht-relative cht-flex-1">
                <Search className="cht-absolute cht-left-3 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={15} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search services…"
                  className="cht-w-full cht-pl-9 cht-pr-4 cht-py-2.5 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm cht-outline-none focus:cht-border-[#3e57da] cht-transition-colors"
                />
              </div>
              <div className="cht-flex cht-gap-2 cht-flex-wrap">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`cht-px-4 cht-py-2 cht-rounded-xl cht-text-sm cht-font-medium cht-transition-all ${category === cat
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-white cht-border cht-border-gray-200 cht-text-gray-600 hover:cht-border-[#3e57da]/40"
                      }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid layout panel container */}
            <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-6">
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
                : filtered.length > 0
                  ? filtered.map((service) => (
                    <ServiceCard key={service.id} service={service} onSelect={setSelectedService} />
                  ))
                  : (
                    <div className="lg:cht-col-span-3 cht-text-center cht-py-16 cht-text-gray-500">
                      No active verification services match your filter layout matrix.
                    </div>
                  )}
            </div>
          </>
        )}
      </main>
    </section>
  );
};

export default ServicesPage;